import React, { useState, useEffect } from "react";
import toast, { Toaster } from 'react-hot-toast';
import HeaderNavbar from '../components/HeaderNavbar';
import LazyImage from '../components/LazyImage';
import Footer from '../components/Footer';
import ProjectVisibilityStatus from '../components/ProjectVisibilityStatus';
import Spinner from 'react-bootstrap/Spinner';
import config from "../config";
import api from "../lib/request.js";
import { useAuthContext } from '../contexts/AuthContext';
import { sanitizeMarkdownPreviewText } from '../lib/markdownPreview';
import './ProjectsList.css';

function ProjectsList() {
  const { isAuthenticated, isSiteAdmin, canAdminOrganization } = useAuthContext();
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [data, setData] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState({
    title: '',
    subTitle: '',
    description: '',
    organizationId: '',
    file: null
  });

  async function fetchData() {
    try {
      const response = await api.get(`/api/v1/projects`);
      setData(response);
    } catch (e) {
      console.log('e===', e);
    }
  }

  async function fetchOrganizations() {
    try {
      setLoadingOrganizations(true);
      const response = await api.get(`/api/v1/organizations`);
      setOrganizations(response?.organizations || []);
    } catch (e) {
      console.log('Error fetching organizations', e);
      toast.error('Failed to load organizations.');
    } finally {
      setLoadingOrganizations(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  function handleShowModal(type, data) {
    if (type === 'new' && (!organizations || organizations.length === 0)) {
      toast.error('Create an organization before adding a project.');
      return;
    }
    setModalType(type);
    if (type === 'edit' && data) {
      setModalData({
        ...data,
        organizationId: data?.org !== undefined ? String(data.org) : '',
        file: null
      });
    } else {
      setModalData({
        title: '',
        subTitle: '',
        description: '',
        organizationId: organizations.length ? String(organizations[0].id) : '',
        file: null
      });
    }
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
  }

  function handleInputChange(event) {
    setModalData({
      ...modalData,
      [event.target.name]: event.target.value,
    });
  }

  function handleFileChange(event) {
     const file = event.target.files[0];
     setModalData((prevData) => ({
       ...prevData,
       file: file,
     }));
   }

  async function handleSubmit(event) {
     // Prevent form being submitted through HTML
     event.preventDefault();
     setSavingProject(true);

     if (!modalData?.title.trim()) {
      alert('Title is required')
      setSavingProject(false);
      return;
     }
     if (!modalData?.organizationId) {
      alert('Organization is required')
      setSavingProject(false);
      return;
     }

     // construct the URL based on the action
     const url = modalType === 'edit' 
       ? `/api/v1/projects/${modalData.slugURL}` 
       : `/api/v1/projects`;

     // choose method based on the action
     const method = modalType === 'edit' ? 'put' : 'post';

     // Create FormData
     const formData = new FormData();
     formData.append("title", modalData.title);
     formData.append("subTitle", modalData.subTitle);
     formData.append("description", modalData.description);
     formData.append("organizationId", modalData.organizationId);
     
     // Check if a file has been selected and append it
     if (modalData.file) {
       formData.append("images", modalData.file);
     }

     const requestData = {
       method: method,
       url: url,
       data: formData,
       headers: { "Content-Type": "multipart/form-data" }
     };

     try {
       const response = await api(requestData);
       // Update the state and close the modal
       if (response.ok === true) {
         // Fetch data again
         document.getElementById('theform').reset();
         fetchData();
         handleCloseModal();
         toast.success(`Project successfully ${modalType === 'edit' ? 'edited' : 'created'}`)
       } else {
         if (response.error) {
          // TODO: This code section is never reached, instead it goes directly to the catch clause below. 
           toast.error(response.message);
         }
       }
     } catch (error) {
      toast.error(error?.message || error.toString());
     } finally {
      setSavingProject(false);
     }
   }

  function getDraftCount(guides) {
    let draftTotal = 0;
    if (guides && guides.length) {
      draftTotal = guides.filter(g => g.published === false).length;
    }
    return draftTotal;
   }

  function getPendingCommentsCount(project, canManageProject) {
    if (!canManageProject) return 0;
    const count = Number(project?.pendingCommentsCount || 0);
    return count > 0 ? count : 0;
  }

  function getOrganizationName(orgId) {
    const numericId = Number(orgId);
    const organization = organizations.find((org) => org.id === numericId);
    if (organization) {
      return organization.name;
    }
    if (!Number.isNaN(numericId)) {
      return `ID ${numericId}`;
    }
    return 'Unknown';
  }

  if (!data) {
    return (
      <>
      <HeaderNavbar/>
      <div className="projects-list-page">
        <div className="projects-list-loading">
          <Spinner animation="border" />
        </div>
      </div>
      <Footer/>
      </>
    );
  }

  const canCreateProjects = isAuthenticated && isSiteAdmin;
  const userCanEditProject = (projectOrgId) => isAuthenticated && canAdminOrganization(projectOrgId);
  const getProjectStatus = (project) => (project?.status || '').toString().toLowerCase();

  return (
    <>
     <HeaderNavbar/>
      <div className="projects-list-page">
        <div className="projects-list-toolbar">
          <div>
            <h1 className="projects-list-title" key="nav3">Explore and Build</h1>
          </div>
          {canCreateProjects ? (
            <button
              key="nav2"
              className="btn btn-sm btn-outline-dark"
              onClick={() => handleShowModal('new')}
              disabled={!organizations.length || loadingOrganizations}
              title={organizations.length ? '' : 'Create an organization first'}
            >
              Create Project
            </button>
          ) : null}
        </div>

      <div className="row g-4 projects-list-grid">
      {(data || []).length === 0 ? (
        <div className="col-12">
          <div className="projects-list-empty">No projects available.</div>
        </div>
      ) : null}
      {data.map((project) => {
        const status = getProjectStatus(project);
        const isPending = status === 'pending';
        const canEditCurrentProject = userCanEditProject(project.org) && !isPending;
        const draftCount = getDraftCount(project?.guides);
        const pendingCommentsCount = getPendingCommentsCount(project, canEditCurrentProject);
        const descriptionPreview = sanitizeMarkdownPreviewText(project?.description);
        return (
        <div className="col-12 col-lg-6" key={project.id || project.slugURL}>
          <article className="projects-list-card">
            <a href={`${config.prefixPath}/projects/${project.slugURL}`} className="projects-list-image-link">
              <LazyImage 
                src={project.image || "https://placehold.co/2500x280"} 
                className="projects-list-image" 
                alt={project?.title || 'Project image'}
                style={{height: '220px', width: '100%', objectFit: 'cover', objectPosition: 'center'}}/>
            </a>
            <div className="projects-list-card-body">
              <div className="projects-list-meta-row">
                <span className="projects-list-org">By {getOrganizationName(project.org)}</span>
                {isPending ? <span className="projects-list-status projects-list-status-pending">Pending approval</span> : null}
                {!isPending ? <ProjectVisibilityStatus project={project} /> : null}
              </div>
              <a href={`${config.prefixPath}/projects/${project.slugURL}`} className="projects-list-title-link">
                <h2 className="projects-list-card-title">{project.title}</h2>
              </a>
              {project?.subTitle ? <p className="projects-list-card-subtitle">{project.subTitle}</p> : null}
              {descriptionPreview ? (
                <p className="projects-list-card-description">{descriptionPreview}</p>
              ) : (
                <p className="projects-list-card-description projects-list-card-description-empty">No description provided.</p>
              )}
              <p className="projects-list-stats">
                <span className="projects-list-stat">{project?.guides?.length || 0} guide{(project?.guides?.length || 0) === 1 ? '' : 's'}/BOM{(project?.guides?.length || 0) === 1 ? '' : 's'}</span>
                {canEditCurrentProject && draftCount > 0 ? (
                  <span className="projects-list-stat projects-list-stat-muted">{draftCount} draft{draftCount === 1 ? '' : 's'}</span>
                ) : null}
                {pendingCommentsCount > 0 ? (
                  <span className="projects-list-stat projects-list-stat-warning">{pendingCommentsCount} pending comment{pendingCommentsCount === 1 ? '' : 's'}</span>
                ) : null}
              </p>
            </div>
          </article>
        </div>
      )})}
      </div>
      </div>
      <div className={`modal ${showModal ? 'show' : ''}`} tabIndex="-1" style={showModal ? { display: 'block' } : { display: 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{modalType === 'new' ? 'New Project' : 'Edit Project'}</h5>
            </div>
            <div className="modal-body">
              <form id="theform" className="needs-validation">
                <div className="form-group">
                  <label>Title</label>
                  <input required type="text" className="form-control" name="title" value={modalData.title} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <input type="text" className="form-control" name="subTitle" value={modalData.subTitle} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-control" name="description" value={modalData.description} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Organization</label>
                  <select
                    className="form-control"
                    name="organizationId"
                    value={modalData.organizationId}
                    onChange={handleInputChange}
                    disabled={loadingOrganizations || (!isSiteAdmin && modalType === 'edit')}
                  >
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  {!organizations.length && (
                    <small className="form-text text-muted">
                      Use the CLI script to create organizations before adding projects.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <input onChange={handleFileChange} className="form-control" name="images" type="file" id={`fileInput`} accept="image/*;capture=camera" multiple={false}/>
                </div>
              </form>
            </div>
            <div className="modal-footer">
            {savingProject ? <Spinner animation="border" /> : <>
              <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleCloseModal}>Close</button>
              <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleSubmit}>Save changes</button>
              </>
              }
            </div>
          </div>
        </div>
      </div>

      <Toaster position="bottom-left" toastOptions={{duration: 5000, style: {background: '#e3e3e3'}}}/>
      <Footer/>
    </>
  );
}

export default ProjectsList;
